import React from 'react';

import WFDLogo from '../../components/Logo';
import {
  useCreateCoSignerRequest,
  useGetCoSignerRequestsAndApplicantInfo,
} from '../../hooks/useCoSignerRequest';
import { useParams } from 'react-router-dom';
import ErrorFullScreen from '../../components/ErrorFullScreen';
import LoadingFullScreen from '../../components/LoadingFullScreen';
import { dealApplicantInitialData } from '../../contexts/FormContext';
import CoSignerForm from './CoSignerForm';
import { CoSignerRequest } from '../../types/coSignerRequests';
import axios from 'axios';
import { validateSchema } from '../../validations';
import { toast } from 'react-toastify';
import { DealApplicantInfo } from '../../types/applicants';
import { formatDate, formatPhoneNumber } from '../../utils/functions';
import SuccessIcon from '../../components/Success';
import BaselineAccessTime from '../../components/BaselineAccessTime';
import ErrorIcon from '../../components/Error';
import Tooltip from '../../components/Tooltip';
import { Visibility } from '@material-ui/icons';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@material-ui/core';
import CloseDialogButton from '../../components/CloseDialogButton';

const parseApplicant = (
  applicantInfo: DealApplicantInfo
): DealApplicantInfo => {
  const { currentApt, previousApt, ...restApplicantInfo } = applicantInfo;
  return {
    ...(Object.fromEntries(
      Object.entries(restApplicantInfo).map(([key, value]) => [
        key,
        typeof value === 'string' ? value.trim() : value,
      ])
    ) as typeof restApplicantInfo),
    birthDate: new Date(applicantInfo?.birthDate as string),
    currentAddress: [
      applicantInfo?.currentAddressNumber,
      applicantInfo?.currentAddress,
      currentApt,
    ].join(' '),
    previousAddress: [
      applicantInfo?.previousAddressNumber,
      applicantInfo?.previousAddress,
      previousApt,
    ].join(' '),
    driverLicenseIssued: new Date(applicantInfo?.driverLicenseIssued as string),
    driverLicenseExpires: new Date(
      applicantInfo?.driverLicenseExpires as string
    ),
  };
};

export default function CoSignerPage() {
  const [state, setState] = React.useState<DealApplicantInfo>(
    dealApplicantInitialData
  );
  const [moreInfoDialog, setMoreInfoDialog] = React.useState<{
    open: boolean;
    coSignerRequest: CoSignerRequest | null;
  }>({
    open: false,
    coSignerRequest: null,
  });
  const [captchaState, setCaptchaState] = React.useState<string | null>(null);
  const params = useParams<{
    dealId: string;
  }>();
  const dealId = params.dealId;
  const { data, isLoading, error, refetch } =
    useGetCoSignerRequestsAndApplicantInfo({
      query: { dealId },
    });
  const {
    createCoSignerRequest,
    loading: createRequestLoading,
    success: createRequestSuccess,
  } = useCreateCoSignerRequest(refetch);
  React.useEffect(() => {
    document.title = 'Fill out co-signer information';
  }, []);
  const [notifyViewedSend, setNotifyViewedSend] = React.useState(false);

  const notifyViewedCoSignerRequests = async (
    coSignerRequests: CoSignerRequest[]
  ) => {
    for (let i = 0; i < coSignerRequests.length; i++) {
      const coSignerRequest = coSignerRequests[i];
      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/co_signer_request/${coSignerRequest._id}/viewed`
        );
      } catch (error) {
        console.error('Error notifying viewed co-signre request', error);
      }
    }
  };

  React.useEffect(() => {
    if (notifyViewedSend) return;
    if (isLoading || !data?.coSignerRequests) return;
    setNotifyViewedSend(true);
    notifyViewedCoSignerRequests(data?.coSignerRequests);
  }, [isLoading, notifyViewedSend, data?.coSignerRequests]);
  React.useEffect(() => {
    if (createRequestSuccess) {
      setState(dealApplicantInitialData);
    }
  }, [createRequestSuccess]);
  if (error) {
    return <ErrorFullScreen />;
  }
  if (isLoading || !data?.applicantInfo || !data.names) {
    return <LoadingFullScreen />;
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const { valid } = validateSchema('new_co_signer_request', {
      applicantInfo: state,
      dealId,
    });
    if (valid && state) {
      createCoSignerRequest({
        applicantInfo: parseApplicant(state),
        dealId,
        coSignerRequestId:
          data?.coSignerRequests?.length === 1 &&
          data?.coSignerRequests[0]?.data?.info?.createdBy === 'manager'
            ? data?.coSignerRequests[0]?._id
            : undefined,
        token: captchaState,
      });
    } else {
      toast.error('Form is not valid! Please check the fields.');
    }
  }
  const filteredCoSignerRequests = data?.coSignerRequests?.filter(
    (coSignerRequest) =>
      Object.keys(coSignerRequest?.data?.info?.applicantInfo ?? {})?.length
  );
  return (
    <div className="flex items-center lg:w-1/2 xl:w-1/3 m-auto  p-10 h-screen  flex-col gap-y-5">
      <div>
        <WFDLogo />
      </div>
      <div className=" w-full mb-10 flex flex-wrap gap-3 sm:gap-5 rounded-md bg-[#F5F7FB] py-6 px-8">
        <div className="flex flex-col gap-y-2">
          <div className="font-medium text-xl text-left">
            Hello {data.names},
          </div>
          <div className="font-medium text-xl text-left">
            In order to process your loan application we need you to fill out
            the co-signer information.
          </div>
        </div>
      </div>
      {filteredCoSignerRequests?.length > 0 && (
        <div className=" w-full mb-10 flex flex-wrap gap-3 sm:gap-5 rounded-md bg-[#F5F7FB] py-6 px-8">
          <div className="w-full font-medium text-xl text-center">
            Previous submissions
          </div>
          {filteredCoSignerRequests.map((coSignerRequest, index) => (
            <div className="w-full flex flex-col" key={index}>
              {index !== 0 && (
                <div className="w-full my-4 flex h-[1px] bg-gray-300"></div>
              )}
              <div className=" w-full flex  items-center justify-between gap-x-2 ">
                <div className="flex w-full flex-col gap-y-2">
                  <span className="font-medium text-lg">
                    Co-signer:{' '}
                    {coSignerRequest?.data?.info?.applicantInfo?.firstName}{' '}
                    {coSignerRequest?.data?.info?.applicantInfo?.lastName}
                  </span>
                  <span className="font-normal text-sm text-gray-500">
                    Submitted at:{' '}
                    {formatDate(coSignerRequest.createdAt, 'medium', true)}
                  </span>
                </div>

                <div className="flex flex-row gap-y-2 gap-x-2 items-center h-16 justify-end min-w-[140px]">
                  <Tooltip text="View more info">
                    <IconButton
                      onClick={() =>
                        setMoreInfoDialog({ open: true, coSignerRequest })
                      }
                    >
                      <Visibility style={{ color: '#000000' }} />
                    </IconButton>
                  </Tooltip>
                  {coSignerRequest?.data?.info?.status === 'approved' ? (
                    <SuccessIcon />
                  ) : coSignerRequest?.data?.info?.status === 'pending' ? (
                    <Tooltip text="Your co-signer submission is being processed by the F&I team. In case of any issues, you will be notified.">
                      <BaselineAccessTime />
                    </Tooltip>
                  ) : coSignerRequest?.data?.info?.status === 'rejected' ? (
                    <ErrorIcon />
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className=" w-full mb-10 flex flex-wrap gap-3 sm:gap-5 rounded-md  py-6 ">
        <form onSubmit={handleSubmit} id="new-co-signer-request-form">
          <CoSignerForm
            createRequestLoading={createRequestLoading}
            createRequestSuccess={createRequestSuccess}
            captchaState={captchaState}
            setCaptchaState={setCaptchaState}
            mainApplicantInfo={data.applicantInfo}
            state={state}
            setState={setState}
          />
        </form>
      </div>
      <MoreInfoDialog
        open={moreInfoDialog.open}
        coSignerRequest={moreInfoDialog.coSignerRequest}
        onClose={() =>
          setMoreInfoDialog({ open: false, coSignerRequest: null })
        }
      />
    </div>
  );
}

type MoreInfoDialogProps = {
  open: boolean;
  coSignerRequest: CoSignerRequest | null;
  onClose: () => void;
};
const MoreInfoDialog = ({
  open,
  coSignerRequest,
  onClose,
}: MoreInfoDialogProps) => {
  if (!coSignerRequest) return null;
  return (
    <Dialog
      id="more-info-dialog"
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={onClose}
    >
      <DialogTitle id="more-info-dialog-title">
        Detailed submission information
      </DialogTitle>
      <CloseDialogButton closeFunction={onClose} />

      <DialogContent>
        <div className="flex flex-col gap-y-2">
          <span className="font-medium text-sm text-gray-500">
            Status: {coSignerRequest.data.info.status}
          </span>
          <span className="font-normal text-sm text-gray-500">
            First name:{' '}
            {coSignerRequest?.data?.info?.applicantInfo?.firstName ?? 'N/A'}
          </span>
          <span className="font-normal text-sm text-gray-500">
            Last name:{' '}
            {coSignerRequest?.data?.info?.applicantInfo?.lastName ?? 'N/A'}
          </span>
          <span className="font-normal text-sm text-gray-500">
            Email: {coSignerRequest?.data?.info?.applicantInfo?.email ?? 'N/A'}
          </span>
          <span className="font-normal text-sm text-gray-500">
            Phone:{' '}
            {formatPhoneNumber(
              coSignerRequest.data.info.applicantInfo?.mobilePhone ?? ''
            ) ?? 'N/A'}
          </span>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          id="cancel"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
